import { api } from ".";
import {
  IEdiConversionFile,
  IEdiUploadMapping,
} from "../../types/ediConversionFile";

// Response from POST /edi-conversion/upload-init — step 1 of the direct-to-S3 upload flow.
// The client then PUTs the file bytes to `uploadUrl` (and `upload837Url` if present), and
// finally calls /upload-complete with the uploadBatchId to kick off async parsing.
export interface IEdiUploadInitResponse {
  uploadBatchId: string;
  ediConversionFileIds: string[];
  uploadUrl: string;
  upload837Url?: string;
  uploadExpiresAt: string;
}

// One provider in the "Reprocess all" picker — a provider that has EDI files, with the aggregate
// stats shown in the modal once it is selected.
export interface IProviderReprocessOption {
  providerId: string;
  providerName: string;
  fileCount: number;
  totalRecords: number;
  oldestFileAt: string;
  newestFileAt: string;
}

// Outcome of a bulk per-provider reprocess enqueue.
export interface IBulkReprocessResult {
  providerId: string;
  totalFiles: number;
  queued: number;
  skippedNoOriginal: number;
}

const ediConversionQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    getAllEdiConversionFiles: builder.query<
      { data: IEdiConversionFile[]; message: string },
      { search?: string }
    >({
      query: (params) => ({
        url: "/edi-conversion",
        params,
      }),
      providesTags: ["EdiConversion"],
    }),
    getEdiConversionFileById: builder.query<
      { data: IEdiConversionFile; message: string },
      string
    >({
      query: (id) => `/edi-conversion/${id}`,
    }),
    // CLE-2236: one upload now carries 1–3 provider-mapping rules and fans out into one
    // EdiConversionFile per rule, so the response is an array.
    convertAndUploadEdi: builder.mutation<
      { data: IEdiConversionFile[]; message: string; warnings?: string[] },
      {
        files: File[];
        displayName: string;
        description?: string;
        mappings: IEdiUploadMapping[];
        files837?: File[];
      }
    >({
      query: ({ files, displayName, description, mappings, files837 }) => {
        const formData = new FormData();
        files.forEach((f) => formData.append("files", f));
        if (files837) {
          files837.forEach((f) => formData.append("files837", f));
        }
        formData.append("displayName", displayName);
        if (description) {
          formData.append("description", description);
        }
        formData.append("mappings", JSON.stringify(mappings));

        return {
          url: "/edi-conversion/convert",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["EdiConversion"],
    }),
    getEdiConversionFileDownloadUrl: builder.query<
      { url: string; message: string },
      string
    >({
      query: (id) => `/edi-conversion/${id}/download`,
    }),
    deleteEdiConversionFile: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/edi-conversion/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["EdiConversion"],
    }),
    reprocessEdiFile: builder.mutation<
      { data: IEdiConversionFile; message: string },
      string
    >({
      query: (id) => ({
        url: `/edi-conversion/${id}/reprocess`,
        method: "POST",
      }),
      invalidatesTags: ["EdiConversion"],
    }),
    // Providers that have EDI files, each with file count / total records / date span — powers the
    // "Reprocess all for a provider" modal's picker and at-a-glance stats.
    getProviderReprocessOptions: builder.query<
      { data: IProviderReprocessOption[]; message: string },
      void
    >({
      query: () => "/edi-conversion/provider-reprocess-options",
      providesTags: ["EdiConversion"],
    }),
    // Bulk-enqueue a reprocess for every EDI file belonging to a provider, oldest → newest.
    reprocessAllForProvider: builder.mutation<
      { data: IBulkReprocessResult; message: string },
      { providerId: string }
    >({
      query: (body) => ({
        url: "/edi-conversion/reprocess-all",
        method: "POST",
        body,
      }),
      invalidatesTags: ["EdiConversion"],
    }),
    runAiAnalysis: builder.mutation<
      { data: { claimCount: number }; message: string },
      string
    >({
      query: (id) => ({
        url: `/edi-conversion/${id}/run-ai-analysis`,
        method: "POST",
      }),
      invalidatesTags: ["EdiConversion"],
    }),
    // Direct-upload step 1: allocate pre-signed S3 PUT URLs without sending bytes through
    // the API. Client then PUTs to uploadUrl and calls /upload-complete.
    initiateEdiUploadBatch: builder.mutation<
      { data: IEdiUploadInitResponse; message: string },
      {
        displayName: string;
        description?: string;
        originalFilename: string;
        fileSizeBytes: number;
        has837?: boolean;
        // When has837 is set, these let the backend allocate an S3 key that keeps the real
        // extension (e.g. .zip vs .837 vs .txt) and re-check the 837's size against the cap
        // after the client PUT completes.
        original837Filename?: string;
        file837SizeBytes?: number;
        mappings: IEdiUploadMapping[];
      }
    >({
      query: (body) => ({
        url: "/edi-conversion/upload-init",
        method: "POST",
        body,
      }),
    }),
    // Direct-upload step 2: HEAD-verify the S3 object and kick off async parsing.
    confirmEdiUploadBatch: builder.mutation<
      { message: string },
      { uploadBatchId: string }
    >({
      query: (body) => ({
        url: "/edi-conversion/upload-complete",
        method: "POST",
        body,
      }),
      invalidatesTags: ["EdiConversion"],
    }),
  }),
});

export const {
  useGetAllEdiConversionFilesQuery,
  useGetEdiConversionFileByIdQuery,
  useConvertAndUploadEdiMutation,
  useGetEdiConversionFileDownloadUrlQuery,
  useDeleteEdiConversionFileMutation,
  useReprocessEdiFileMutation,
  useGetProviderReprocessOptionsQuery,
  useReprocessAllForProviderMutation,
  useRunAiAnalysisMutation,
  useInitiateEdiUploadBatchMutation,
  useConfirmEdiUploadBatchMutation,
} = ediConversionQueries;
